import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ShellBar,
  Card,
  Title,
  Text,
  Label,
  Input,
  Select,
  Option,
  Button,
  ProgressIndicator,
  FlexBox,
  MessageStrip
} from '@ui5/webcomponents-react';
import { organizationApi } from '../services/api';
import { Industry, EmployeeCount, Language, OrganizationCreate } from '@dsgvo-buddy/types';
import '@ui5/webcomponents-icons/dist/AllIcons.js';

interface FormData {
  // Schritt 1: Unternehmensdaten
  name: string;
  address: string;
  industry: Industry | '';
  employee_count: EmployeeCount | '';

  // Schritt 2: Ansprechpartner
  contact_person: string;
  contact_email: string;
  contact_phone: string;

  // Schritt 3: Datenschutz-Konfiguration
  dsb_name: string;
  dsb_email: string;
  dsb_phone: string;
  default_language: Language;
  default_retention_period: number;
}

export default function SetupWizardPage() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<FormData>({
    name: '',
    address: '',
    industry: '',
    employee_count: '',
    contact_person: '',
    contact_email: '',
    contact_phone: '',
    dsb_name: '',
    dsb_email: '',
    dsb_phone: '',
    default_language: Language.DE,
    default_retention_period: 36
  });

  const totalSteps = 3;

  const updateField = (field: keyof FormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError(null);
  };

  const validateStep = (step: number): boolean => {
    switch (step) {
      case 1:
        if (!formData.name.trim()) {
          setError('Bitte geben Sie einen Unternehmensnamen ein');
          return false;
        }
        if (!formData.address.trim()) {
          setError('Bitte geben Sie eine Adresse ein');
          return false;
        }
        return true;

      case 2:
        if (!formData.contact_person.trim()) {
          setError('Bitte geben Sie einen Ansprechpartner ein');
          return false;
        }
        if (!formData.contact_email.trim()) {
          setError('Bitte geben Sie eine E-Mail-Adresse ein');
          return false;
        }
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.contact_email)) {
          setError('Bitte geben Sie eine gültige E-Mail-Adresse ein');
          return false;
        }
        return true;

      case 3:
        if (formData.dsb_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.dsb_email)) {
          setError('Bitte geben Sie eine gültige DSB E-Mail-Adresse ein');
          return false;
        }
        if (formData.default_retention_period < 1) {
          setError('Die Löschfrist muss mindestens 1 Monat betragen');
          return false;
        }
        return true;

      default:
        return true;
    }
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      if (currentStep < totalSteps) {
        setCurrentStep(currentStep + 1);
      }
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      setError(null);
    }
  };

  const handleSubmit = async () => {
    if (!validateStep(currentStep)) return;

    setLoading(true);
    setError(null);

    try {
      const orgData: OrganizationCreate = {
        name: formData.name,
        address: formData.address,
        contact_person: formData.contact_person,
        contact_email: formData.contact_email,
        contact_phone: formData.contact_phone || undefined,
        dsb_name: formData.dsb_name || undefined,
        dsb_email: formData.dsb_email || undefined,
        dsb_phone: formData.dsb_phone || undefined,
        industry: formData.industry || undefined,
        employee_count: formData.employee_count || undefined,
        default_language: formData.default_language,
        default_retention_period: formData.default_retention_period
      };

      const response = await organizationApi.create(orgData);

      if (response.success) {
        navigate('/dashboard');
      } else {
        setError(response.error || 'Fehler beim Erstellen der Organisation');
      }
    } catch (err) {
      setError('Ein unerwarteter Fehler ist aufgetreten');
    } finally {
      setLoading(false);
    }
  };

  const renderStep1 = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      <div className="form-group">
        <Label required>Unternehmensname</Label>
        <Input
          value={formData.name}
          onInput={(e: any) => updateField('name', e.target.value)}
          placeholder="z.B. Musterfirma GmbH"
          style={{ width: '100%' }}
        />
      </div>

      <div className="form-group">
        <Label required>Adresse</Label>
        <Input
          value={formData.address}
          onInput={(e: any) => updateField('address', e.target.value)}
          placeholder="Straße, PLZ Ort"
          style={{ width: '100%' }}
        />
      </div>

      <div className="form-group">
        <Label>Branche</Label>
        <Select
          value={formData.industry}
          onChange={(e: any) => updateField('industry', e.detail.selectedOption.value)}
          style={{ width: '100%' }}
        >
          <Option value="">Bitte wählen...</Option>
          <Option value={Industry.IT}>IT & Technologie</Option>
          <Option value={Industry.HEALTHCARE}>Gesundheitswesen</Option>
          <Option value={Industry.FINANCE}>Finanzwesen</Option>
          <Option value={Industry.RETAIL}>Einzelhandel</Option>
          <Option value={Industry.MANUFACTURING}>Produktion</Option>
          <Option value={Industry.SERVICES}>Dienstleistungen</Option>
          <Option value={Industry.EDUCATION}>Bildung</Option>
          <Option value={Industry.GOVERNMENT}>Öffentliche Verwaltung</Option>
          <Option value={Industry.OTHER}>Sonstiges</Option>
        </Select>
      </div>

      <div className="form-group">
        <Label>Mitarbeiterzahl</Label>
        <Select
          value={formData.employee_count}
          onChange={(e: any) => updateField('employee_count', e.detail.selectedOption.value)}
          style={{ width: '100%' }}
        >
          <Option value="">Bitte wählen...</Option>
          <Option value={EmployeeCount.SMALL}>1-10 Mitarbeiter</Option>
          <Option value={EmployeeCount.MEDIUM}>11-50 Mitarbeiter</Option>
          <Option value={EmployeeCount.LARGE}>51-250 Mitarbeiter</Option>
          <Option value={EmployeeCount.XLARGE}>251-1000 Mitarbeiter</Option>
          <Option value={EmployeeCount.ENTERPRISE}>1000+ Mitarbeiter</Option>
        </Select>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      <div className="form-group">
        <Label required>Name des Ansprechpartners</Label>
        <Input
          value={formData.contact_person}
          onInput={(e: any) => updateField('contact_person', e.target.value)}
          placeholder="Max Mustermann"
          style={{ width: '100%' }}
        />
      </div>

      <div className="form-group">
        <Label required>E-Mail</Label>
        <Input
          type="Email"
          value={formData.contact_email}
          onInput={(e: any) => updateField('contact_email', e.target.value)}
          placeholder="max.mustermann@firma.de"
          style={{ width: '100%' }}
        />
      </div>

      <div className="form-group">
        <Label>Telefon</Label>
        <Input
          type="Tel"
          value={formData.contact_phone}
          onInput={(e: any) => updateField('contact_phone', e.target.value)}
          placeholder="+49 123 456789"
          style={{ width: '100%' }}
        />
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      <Text style={{ marginBottom: '0.5rem' }}>
        <strong>Datenschutzbeauftragter (optional)</strong>
      </Text>
      <Text style={{ fontSize: '0.875rem', color: 'var(--sapNeutralColor)', marginBottom: 'var(--sapSpaceMedium)' }}>
        Wenn Sie einen Datenschutzbeauftragten benannt haben, können Sie dessen Daten hier hinterlegen.
      </Text>

      <div className="form-group">
        <Label>Name des DSB</Label>
        <Input
          value={formData.dsb_name}
          onInput={(e: any) => updateField('dsb_name', e.target.value)}
          placeholder="Optional"
          style={{ width: '100%' }}
        />
      </div>

      <div className="form-group">
        <Label>E-Mail des DSB</Label>
        <Input
          type="Email"
          value={formData.dsb_email}
          onInput={(e: any) => updateField('dsb_email', e.target.value)}
          placeholder="Optional"
          style={{ width: '100%' }}
        />
      </div>

      <div className="form-group">
        <Label>Telefon des DSB</Label>
        <Input
          type="Tel"
          value={formData.dsb_phone}
          onInput={(e: any) => updateField('dsb_phone', e.target.value)}
          placeholder="Optional"
          style={{ width: '100%' }}
        />
      </div>

      <div style={{ borderTop: '1px solid var(--sapField_BorderColor)', paddingTop: 'var(--sapSpaceMedium)', marginTop: 'var(--sapSpaceMedium)' }}>
        <Text style={{ marginBottom: '0.5rem' }}>
          <strong>Standard-Einstellungen</strong>
        </Text>

        <div className="form-group">
          <Label>Standardsprache</Label>
          <Select
            value={formData.default_language}
            onChange={(e: any) => updateField('default_language', e.detail.selectedOption.value)}
            style={{ width: '100%' }}
          >
            <Option value={Language.DE}>Deutsch</Option>
            <Option value={Language.EN}>Englisch</Option>
          </Select>
        </div>

        <div className="form-group">
          <Label>Standard-Löschfrist (Monate)</Label>
          <Input
            type="Number"
            value={formData.default_retention_period.toString()}
            onInput={(e: any) => updateField('default_retention_period', parseInt(e.target.value) || 1)}
            style={{ width: '100%' }}
          />
          <Text style={{ fontSize: '0.75rem', color: 'var(--sapNeutralColor)', marginTop: '0.25rem' }}>
            Standard: 36 Monate (3 Jahre)
          </Text>
        </div>
      </div>
    </div>
  );

  const stepTitles = [
    'Unternehmensdaten',
    'Ansprechpartner',
    'Datenschutz-Konfiguration'
  ];

  return (
    <div>
      <ShellBar
        primaryTitle="DSGVOeasy"
        secondaryTitle="Organisations-Setup"
        logo={<img src="/logo.png" alt="DSGVOeasy" style={{ height: '2rem', width: 'auto' }} />}
      />

      <div className="page">
        <div className="page-layout--narrow" style={{ margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
            <Title level="H2">Organisation einrichten</Title>
            <Text>Schritt {currentStep} von {totalSteps}: {stepTitles[currentStep - 1]}</Text>
          </div>

          <ProgressIndicator
            value={(currentStep / totalSteps) * 100}
            style={{ marginBottom: '1.5rem' }}
          />

          <Card>
            <div style={{ padding: '1.5rem' }}>
              {error && (
                <MessageStrip
                  design="Negative"
                  hideCloseButton
                  style={{ marginBottom: '1rem' }}
                >
                  {error}
                </MessageStrip>
              )}

              {currentStep === 1 && renderStep1()}
              {currentStep === 2 && renderStep2()}
              {currentStep === 3 && renderStep3()}

              <FlexBox
                justifyContent="SpaceBetween"
                style={{ marginTop: '1.5rem', paddingTop: '1.5rem', borderTop: '1px solid var(--sapGroup_ContentBorderColor, #e5e5e5)' }}
              >
                <Button
                  design="Transparent"
                  onClick={handleBack}
                  disabled={currentStep === 1 || loading}
                >
                  Zurück
                </Button>

                {currentStep < totalSteps ? (
                  <Button
                    design="Emphasized"
                    onClick={handleNext}
                    disabled={loading}
                  >
                    Weiter
                  </Button>
                ) : (
                  <Button
                    design="Emphasized"
                    onClick={handleSubmit}
                    disabled={loading}
                  >
                    {loading ? 'Wird erstellt...' : 'Abschließen'}
                  </Button>
                )}
              </FlexBox>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
